/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import { useEffect, useState } from 'react'

import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'

export type RouteLoadingIndicatorVariant = 'progress' | 'spinner'

export type RouteLoadingIndicatorSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'inherit'

const DEFAULT_FADE_MS = 1000

const spinnerSizeClass: Record<Exclude<RouteLoadingIndicatorSize, 'inherit'>, string> = {
  xs: 'size-3',
  sm: 'size-4',
  md: 'size-5',
  lg: 'size-8',
  xl: 'size-10',
}

const spinnerPlaceholderMinH: Record<Exclude<RouteLoadingIndicatorSize, 'inherit'>, string> = {
  xs: 'min-h-3',
  sm: 'min-h-4',
  md: 'min-h-5',
  lg: 'min-h-8',
  xl: 'min-h-10',
}

export interface RouteLoadingIndicatorProps {
  /** When true, the indicator is fully visible; when it becomes false, it fades out then unmounts. */
  loading: boolean
  variant?: RouteLoadingIndicatorVariant
  /** Tailwind classes applied to the spinner icon when `variant="spinner"`. */
  spinnerClassName?: string
  /** Progress and spinner height hint for reserving layout while hidden. */
  size?: RouteLoadingIndicatorSize
  /** Time in ms to stay mounted after loading ends so the opacity transition can finish. */
  fadeOutMs?: number
  className?: string
}

function Placeholder({ variant, size }: { variant: RouteLoadingIndicatorVariant; size: RouteLoadingIndicatorSize }) {
  if (variant === 'progress') {
    return <div className="h-1.5 w-full" />
  }
  const minH = size === 'inherit' ? 'min-h-3' : spinnerPlaceholderMinH[size]
  return <div className={cn('w-full', minH)} />
}

export function RouteLoadingIndicator({
  loading,
  variant = 'progress',
  spinnerClassName,
  size = 'xs',
  fadeOutMs = DEFAULT_FADE_MS,
  className,
}: RouteLoadingIndicatorProps) {
  const [renderIndicator, setRenderIndicator] = useState(false)

  useEffect(() => {
    if (loading) {
      setRenderIndicator(true)
      return
    }
    if (!renderIndicator) {
      return
    }
    const id = setTimeout(() => setRenderIndicator(false), fadeOutMs)
    return () => clearTimeout(id)
  }, [loading, renderIndicator, fadeOutMs])

  if (!renderIndicator) {
    return <Placeholder size={size} variant={variant} />
  }

  const transition = `opacity ${fadeOutMs}ms ease-out`

  if (variant === 'spinner') {
    const resolvedSpinner =
      spinnerClassName ??
      (size === 'inherit' ? 'size-3 text-muted-foreground' : cn('text-muted-foreground', spinnerSizeClass[size]))

    return (
      <div className={cn('flex w-full justify-center', className)} style={{ opacity: loading ? 1 : 0, transition }}>
        <Spinner className={resolvedSpinner} />
      </div>
    )
  }

  return (
    <div
      className={cn('relative h-1.5 w-full overflow-hidden rounded-full bg-muted', className)}
      style={{ opacity: loading ? 1 : 0, transition }}
    >
      <div
        className="absolute inset-y-0 left-0 w-1/3 rounded-full bg-primary"
        style={{
          animation: 'sf-route-loading-indeterminate 1.2s ease-in-out infinite',
        }}
      />
    </div>
  )
}
